import SAAxios from '../../http/requests/service-appointments';
import PAxios from '../../http/requests/projects';
import OAxios from '../../http/requests/orders';

export default {
    /**
     * Opens service appointment edit modal.
     *
     * @param commit Vuex commit function
     * @param id Service appointment ID
     */
    openEditAppointmentModal ({ commit }, id) {
        commit('TOGGLE_APPOINTMENT_EDIT_MODAL', {
            open: true,
            id: id,
            ticket: null,
        });
    },

    /**
     * Opens service appointment edit modal with ticket.
     *
     * @param commit Vuex commit function
     * @param data Data object
     */
    openEditAppointmentModalWithTicket ({ commit }, data) {
        commit('TOGGLE_APPOINTMENT_EDIT_MODAL', {
            open: true,
            id: data.id,
            ticket: data.ticket,
        });
    },

    /**
     * Closes service appointment edit modal.
     *
     * @param commit Vuex commit function
     */
    closeEditAppointmentModal ({ commit }) {
        commit('TOGGLE_APPOINTMENT_EDIT_MODAL', {
            open: false,
            id: null,
            ticket: null,
        });
    },

    /**
     * Creates new or updates existing service appointment.
     *
     * @param commit Vuex commit function
     * @param state Vuex state
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    createUpdateServiceAppointment ({
        commit,
        state,
    }, payload) {
        let data = JSON.parse(JSON.stringify(payload));

        if (!data.id && state.editAppointmentModal.ticket) {
            data.ticket = state.editAppointmentModal.ticket;
        }

        return new Promise((resolve, reject) => {
            console.log('Sending data:', data); // Debug: check structure

            SAAxios.createUpdateServiceAppointment(data)
                .then(response => {
                    commit('SET_APPOINTMENT_EDIT_MODAL_ERRORS', {});

                    resolve(response);
                })
                .catch(error => {
                    if (error.response.status === 422) {
                        commit('SET_APPOINTMENT_EDIT_MODAL_ERRORS', error.response.data.data);
                    }

                    reject(error);
                });
        });
    },

    /**
     * Retrieves projects by autocomplete search term.
     *
     * @param commit Vuex commit function
     * @param state Vuex state
     * @param searchTerm Search term
     *
     * @returns {Promise<unknown>}
     */
    getAutocompleteProjectsBySearchTerm ({
        commit,
        state,
    }, searchTerm) {
        return new Promise((resolve, reject) => {
            PAxios.getAutocompleteProjectsBySearchTerm(searchTerm)
                .then(response => {
                    if (!response.data.success) {
                        reject({ message: response.data.message });
                    }

                    if (state.editAppointmentModal.autocomplete.projectsSearchTerm === searchTerm) {
                        commit('SET_APPOINTMENT_EDIT_MODAL_PROJECTS', response.data.data);
                    }

                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /**
     * Clears project autocomplete suggestions.
     *
     * @param commit Vuex commit function
     */
    clearAutocompleteProjects ({ commit }) {
        commit('SET_APPOINTMENT_EDIT_MODAL_PROJECTS', []);
    },

    /**
     * Retrieves orders by autocomplete search term.
     *
     * @param commit Vuex commit function
     * @param state Vuex state
     * @param payload
     *
     * @returns {Promise<unknown>}
     */
    getAutocompleteOrdersBySearchTerm ({
        commit,
        state,
    }, payload) {
        return new Promise((resolve, reject) => {
            OAxios.getAutocompleteOrdersBySearchTerm(payload.projectId, payload.term)
                .then(response => {
                    if (!response.data.success) {
                        reject({ message: response.data.message });
                    }

                    if (state.editAppointmentModal.autocomplete.ordersSearchTerm === payload.term) {
                        commit('SET_APPOINTMENT_EDIT_MODAL_ORDERS', response.data.data);
                    }

                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /**
     * Sets service appointment edit modal data.
     *
     * @param commit Vuex commit function
     * @param data Data object
     */
    setServiceAppointmentData ({ commit }, data) {
        commit('SET_APPOINTMENT_EDIT_MODAL_DATA', data);
    },

    /**
     * Retrieves service appointment creation information.
     *
     * @param commit Vuex commit function
     *
     * @returns {Promise<unknown>}
     */
    getCreationInformation ({ commit }) {
        return new Promise((resolve, reject) => {
            SAAxios.getCreationInformation()
                .then(response => {
                    if (!response.data.success) {
                        reject({ message: response.data.message });
                    }

                    commit('SET_APPOINTMENT_CREATION_INFO', response.data.data);

                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /**
     * Retrieves service appointment list.
     *
     * @param payload Additional filter properties
     *
     * @returns {Promise<unknown>}
     */
    getServiceAppointmentList ({}, payload) {
        return SAAxios.getServiceAppointmentList(payload.httpOptions);
    },

    /**
     * Retrieves service appointment with given ID.
     *
     * @param commit Vuex commit function
     * @param id Service appointment ID
     *
     * @returns {Promise<unknown>}
     */
    getServiceAppointment ({ commit }, id) {
        return new Promise((resolve, reject) => {
            SAAxios.getServiceAppointment(id)
                .then(response => {
                    if (!response.data.success) {
                        reject({ message: response.data.message });
                    }

                    commit('SET_APPOINTMENT_EDIT_MODAL_DATA', response.data.data);

                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /**
     * Triggers service appointment list refresh.
     *
     * @param commit Vuex commit function
     * @param status Should list be refreshed
     */
    toggleServiceAppointmentListRefresh ({ commit }, status) {
        commit('TOGGLE_LIST_REFRESH', status);
    },

    /**
     * Prefills service appointment edit modal data from project.
     *
     * @param commit Vuex commit function
     * @param id Project ID
     *
     * @returns {Promise<unknown>}
     */
    prefillProjectData ({ commit }, id) {
        return new Promise((resolve, reject) => {
            PAxios.getProject(id)
                .then(response => {

                    const data = {
                        address: response.data.data.address ? response.data.data.address.full_address : '',
                        contact_person: response.data.data.delivery_contact_person ? response.data.data.delivery_contact_person.full_name : '',
                        contact_email: response.data.data.delivery_contact_person ? response.data.data.delivery_contact_person.email : '',
                        contact_phone: response.data.data.delivery_contact_person ? response.data.data.delivery_contact_person.phone : '',
                    };

                    PAxios.getProjectProductionShopDrawings(id)
                        .then(response => {
                            let documents = response.data.data;
                            let lastDocument = false;
                            if (documents.length) {
                                lastDocument = documents.reduce((a, b) => {
                                    return new Date(a.created_at) > new Date(b.created_at) ? a : b;
                                });
                            }

                            if (lastDocument) {
                                data.shop_drawing_url = lastDocument.link;
                            }
                        })
                        .finally(() => {
                            commit('SET_APPOINTMENT_EDIT_MODAL_DATA', data);
                            resolve(response);
                        });
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /**
     * Sets projects autocomplete search term.
     *
     * @param commit Vuex commit function
     * @param term Search term
     */
    setProjectsAutocompleteSearchTerm ({ commit }, term) {
        commit('SET_PROJECTS_SEARCH_TERM', term);
    },

    /**
     * Prefills service appointment edit modal data from order.
     *
     * @param commit Vuex commit function
     * @param id order ID
     *
     * @returns {Promise<unknown>}
     */
    prefillOrderData ({ commit }, { orderId, projectId }) {
        return new Promise((resolve, reject) => {
            OAxios.getOrder(orderId)
                .then(response => {
                    let data = {};
                    if (response.data.data.hasOwnProperty('address') && response.data.data.address
                        && response.data.data.address.hasOwnProperty(
                            'full_address') && response.data.data.address.full_address) {
                        data.address = response.data.data.address.full_address;
                    }
                    if (response.data.data.hasOwnProperty(
                        'delivery_contact_person') && response.data.data.delivery_contact_person) {
                        if (response.data.data.delivery_contact_person.hasOwnProperty('full_name')
                            && response.data.data.delivery_contact_person.full_name) {
                            data.contact_person = response.data.data.delivery_contact_person.full_name;
                        }
                        if (response.data.data.delivery_contact_person.hasOwnProperty('email')
                            && response.data.data.delivery_contact_person.email) {
                            data.contact_email = response.data.data.delivery_contact_person.email;
                        }
                        if (response.data.data.delivery_contact_person.hasOwnProperty('phone')
                            && response.data.data.delivery_contact_person.phone) {
                            data.contact_phone = response.data.data.delivery_contact_person.phone;
                        }
                    }

                    OAxios.getOrderProductionShopDrawings(orderId)
                        .then(response => {
                            let documents = response.data.data;
                            let lastDocument = false;
                            if (documents.length) {
                                lastDocument = documents.reduce((a, b) => {
                                    return new Date(a.created_at) > new Date(b.created_at) ? a : b;
                                });
                            }

                            data.shop_drawing_url = '';
                            if (lastDocument) {
                                data.shop_drawing_url = lastDocument.link;
                            }
                        })
                        .finally(() => {
                            commit('SET_APPOINTMENT_EDIT_MODAL_DATA', data);
                            resolve(response);
                        });
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /**
     * Sets orders autocomplete search term.
     *
     * @param commit Vuex commit function
     * @param term Search term
     */
    setOrdersAutocompleteSearchTerm ({ commit }, term) {
        commit('SET_ORDERS_SEARCH_TERM', term);
    },

    /**
     * Get file upload URL.
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    getUploadUrl: function ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            SAAxios.getFileUploadUrl(payload.projectId, payload.fileName, payload.saType).then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }

                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    },

    /**
     * Add new attachment
     *
     * @param commit Vuex commit function
     * @param state Vuex state
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    addNewAttachment: function ({ commit }, payload) {
        return new Promise((resolve) => {
            commit('APPEND_APPOINTMENT_NEW_ATTACHMENT', payload);
            resolve(true);
        });
    },

    /**
     * Void attachment request.
     *
     * @param commit Vuex commit function
     * @param state Vuex state
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    voidAttachment: function ({
        commit,
        state,
    }, payload) {
        return new Promise((resolve, reject) => {
            SAAxios.voidAttachment(payload.serviceId, payload.attachmentId).then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }

                commit('VOID_APPOINTMENT_ATTACHMENT', payload.attachmentId);

                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    },

    /**
     * Retrieves service appointment filters information.
     *
     * @param commit Vuex commit function
     *
     * @returns {Promise<unknown>}
     */
    getFiltersInformation ({ commit }) {
        return new Promise((resolve, reject) => {
            SAAxios.getFiltersInformation()
                .then(response => {
                    if (!response.data.success) {
                        reject({ message: response.data.message });
                    }

                    commit('SET_FILTERS_INFO', response.data.data);

                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /**
     * Sets service appointment list filters data.
     *
     * @param commit Vuex commit function
     * @param state Vuex state
     * @param dispatch Vuex dispatch function
     * @param data Data object
     */
    setServiceAppointmentListFilters ({
        commit,
        state,
        dispatch,
    }, data) {
        commit('SET_FILTERS_DATA', data);

        dispatch('storeServiceAppointmentListFilters', state.appointmentList.filters);
    },

    /**
     * Opens/closes service appointment list filters sidebar.
     *
     * @param commit Vuex commit function
     * @param status Should sidebar be opened
     */
    toggleServiceAppointmentListFiltersSidebar ({ commit }, status) {
        commit('TOGGLE_FILTER_SIDEBAR', status);
    },

    /**
     * Removes single service appointment list filter.
     *
     * @param commit Vuex commit function
     * @param state Vuex state
     * @param dispatch Vuex dispatch function
     * @param data Data object
     */
    removeServiceAppointmentListFilter ({
        commit,
        state,
        dispatch,
    }, data) {
        commit('REMOVE_FILTER', data);

        dispatch('storeServiceAppointmentListFilters', state.appointmentList.filters);
    },

    /**
     * Gets service appointments list to excel with auth handling.
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    getServiceAppointmentListToExcel ({ commit }, payload) {
        return SAAxios.getServiceAppointmentListToExcel(payload.options);
    },

    /**
     * Stores service appointment list filters in local storage.
     *
     * @param payload Payload data
     */
    storeServiceAppointmentListFilters ({}, payload) {
        localStorage.setItem('serviceAppointmentListFilters', JSON.stringify(payload));
    },

    /**
     * Restores service appointment list filters to the state.
     *
     * @param commit Vuex commit function
     */
    restoreServiceAppointmentListFilters ({ commit }) {
        let filters = localStorage.getItem('serviceAppointmentListFilters');

        if (filters === null) {
            return;
        }

        commit('SET_FILTERS_DATA', JSON.parse(filters));
    },

    /**
     * Cancels service appointment.
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    cancelServiceAppointment ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            SAAxios.cancelServiceAppointment(payload.id, payload.reason)
                .then(response => {
                    commit('SET_APPOINTMENT_EDIT_MODAL_DATA', response.data.data);

                    resolve(response);
                })
                .catch(error => {
                    if (error.response.status === 422) {
                        commit('SET_ACTION_ERRORS', error.response.data.data);
                    }

                    reject(error);
                });
        });
    },

    /**
     * Opens/closes service appointment action with reason block.
     *
     * @param commit Vuex commit function
     * @param data Data object
     */
    toggleActionWithReasonBlock ({ commit }, data) {
        commit('TOGGLE_ACTION_WITH_REASON', data);
    },

    /**
     * Sets action with reason, reason value.
     *
     * @param commit Vuex commit function
     * @param data Reason
     */
    setActionReason ({ commit }, data) {
        commit('SET_ACTION_REASON', data);
    },

    /**
     * Starts service appointment timer
     *
     * @param commit Vuex commit function
     * @param serviceAppointmentId Service Appointment ID
     *
     * @returns {Promise<unknown>}
     */
    startTimer ({ commit }, serviceAppointmentId) {
        return new Promise((resolve, reject) => {
            SAAxios.startTimer(serviceAppointmentId)
                .then(response => {
                    commit('SET_APPOINTMENT_EDIT_MODAL_DATA', response.data.data);

                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /**
     * Finishes service appointment timer
     *
     * @param commit Vuex commit function
     * @param serviceAppointmentId Service Appointment ID
     *
     * @returns {Promise<unknown>}
     */
    finishTimer ({ commit }, serviceAppointmentId) {
        return new Promise((resolve, reject) => {
            SAAxios.finishTimer(serviceAppointmentId)
                .then(response => {
                    commit('SET_APPOINTMENT_EDIT_MODAL_DATA', response.data.data);

                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /**
     * Sets base route for service appointment modal
     *
     * @param commit Vuex commit function
     * @param name base route
     */
    setBaseRoute ({ commit }, name) {
        commit('SET_BASE_ROUTE', name);
    },

    /**
     * Opens/closes service appointment find time modal.
     *
     * @param commit Vuex commit function
     * @param data Data object
     */
    toggleFindTimeModal ({ commit }, data) {
        commit('TOGGLE_FIND_TIME_MODAL', data);
    },

    /**
     * Gets Waze Url
     *
     * @param {string} address
     *
     * @returns {*}
     */
    getWazeLink ({}, address) {
        return new Promise((resolve, reject) => {
            SAAxios.getWazeLink(address)
                .then(response => {
                    resolve(response.data.data.link);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /**
     * Gets appointment coordinates by address
     *
     * @param {function} commit
     * @param {string} address
     *
     * @returns {*}
     */
    getAppointmentLocation ({ commit }, address) {
        return new Promise((resolve, reject) => {
            SAAxios.getLocation(address)
                .then(response => {
                    commit('SET_APPOINTMENT_EDIT_MODAL_DATA', {
                        location: {
                            lat: response.data.data.lat,
                            lng: response.data.data.lng,
                        },
                    });
                    resolve(response.data.data);
                })
                .catch(error => {
                    commit('SET_APPOINTMENT_EDIT_MODAL_DATA', { location: null });
                    reject(error);
                });
        });
    },

    /**
     * Opens service appointment delete modal.
     *
     * @param commit Vuex commit function
     * @param id Service appointment ID
     * @param isOpen is modal opened
     */
    openAppointmentDeleteModal ({ commit }, { id, isOpen}) {
        commit('TOGGLE_APPOINTMENT_DELETE_MODAL', {
            isOpen,
            id,
        });
    },

    /**
     * Deletes service appointment
     *
     * @param commit Vuex commit function
     * @param {int} appointmentId
     *
     * @returns {*}
     */
    deleteServiceAppointment ({ commit }, appointmentId) {
        return new Promise((resolve, reject) => {
            SAAxios.deleteServiceAppointment(appointmentId)
                .then(response => resolve(response.data.data))
                .catch(reject);
        });
    },

    /**
     * Get related service appointments based on projectid or orderid
     * @param commit
     * @param payload{orderId, projectId}
     * @returns {*}
     */
    getRelatedServiceAppointments ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            SAAxios.getRelatedServiceAppointments(payload.orderId, payload.projectId)
                .then(response => {
                    if (!response.data.success) {
                        reject({ message: response.data.message });
                    }

                    commit('SET_RELATED_SERVICE_APPOINTMENTS', response.data.data);
                    resolve(response.data.data);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },
};
